import * as React from "react";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import {
  Button,
  Divider,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Radio,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import { Stack } from "@mui/system";
import CompareIcon from "@mui/icons-material/Compare";
import {
  colors,
  spacing,
  typography,
} from "../../../../../../../../assets/styles/tokens";
import { type TimeframeOption } from "../Performance";

const Puller = styled("div")(({ theme }) => ({
  width: spacing.Spacing["4xl"].$value,
  height: spacing.Spacing.xs.$value,
  backgroundColor: colors.GrayLightMode[300].$value,
  borderRadius: spacing.Radius.xs.$value,
  position: "absolute",
  top: spacing.Spacing.xl.$value,
  left: "calc(50% - 15px)",
  ...theme.applyStyles("dark", {
    backgroundColor: colors.GrayLightMode[300].$value,
  }),
}));

interface PerformanceDataComparisonProps {
  additionalOptions: TimeframeOption[];
  selectedAdditional: TimeframeOption | null;
  onApply: (opt: TimeframeOption | null) => void;
  isMtd: boolean;
  isComparisonActive: boolean;
}

export default function PerformanceDataComparison({
  additionalOptions,
  selectedAdditional,
  onApply,
  isMtd,
  isComparisonActive,
}: PerformanceDataComparisonProps) {
  const [open, setOpen] = React.useState(false);
  const [pendingValue, setPendingValue] = React.useState<string | null>(
    selectedAdditional?.value ?? null,
  );

  const toggleDrawer = (newOpen: boolean) => () => {
    if (newOpen) setPendingValue(selectedAdditional?.value ?? null);
    setOpen(newOpen);
  };

  const handleIconClick = () => {
    if (isComparisonActive) {
      // Clear comparison on second click
      onApply(null);
    } else {
      setPendingValue(selectedAdditional?.value ?? null);
      setOpen(true);
    }
  };

  const handleApply = () => {
    const selected = additionalOptions.find((o) => o.value === pendingValue) ?? null;
    onApply(selected);
    setOpen(false);
  };

  const drawer = (
    <Box role="dataComparison">
      <Puller />
      <Stack justifyContent="end" direction="row" p={1}>
        <IconButton
          aria-label="Close"
          onClick={toggleDrawer(false)}
          sx={{
            width: spacing.Spacing["3xl"].$value,
            height: spacing.Spacing["3xl"].$value,
            color: colors.GrayLightMode[700].$value,
          }}
        >
          <CloseIcon />
        </IconButton>
      </Stack>

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.md.$value,
          fontWeight: typography.Font_weight.Medium.$value,
          color: "rgba(0,0,0,0.87)",
          mb: spacing.Spacing.md.$value,
        }}
      >
        Data Comparison
      </Typography>
      <Divider />

      <Typography
        variant="body1"
        sx={{
          fontSize: typography.Size.Text.xs.$value,
          color: "#00000099",
          mt: spacing.Spacing.md.$value,
        }}
      >
        SELECT COMPARISON PERIOD
      </Typography>

      <Box sx={{ mb: spacing.Spacing["3xl"].$value }}>
        <List disablePadding>
          {additionalOptions.map((option, index) => (
            <React.Fragment key={option.value}>
              <ListItem
                onClick={() => setPendingValue(option.value)}
                sx={{
                  px: "0",
                  cursor: "pointer",
                  WebkitTapHighlightColor: "transparent",
                  "&:hover": { bgcolor: "transparent" },
                  "&:active": { bgcolor: "transparent" },
                  "&:focus": { outline: "none" },
                }}
                secondaryAction={
                  <Radio
                    checked={pendingValue === option.value}
                    onChange={() => setPendingValue(option.value)}
                    value={option.value}
                    size="medium"
                    sx={{
                      color: colors.GrayLightMode[500].$value,
                      "&.Mui-checked": { color: colors.Brand[600].$value },
                      p: 0,
                    }}
                  />
                }
              >
                <ListItemText
                  primary={
                    <Typography
                      sx={{
                        fontSize: typography.Size.Text.sm.$value,
                        lineHeight: typography.Line_height.text.sm.$value,
                        fontWeight: typography.Font_weight.Medium.$value,
                        color: "rgba(0,0,0,0.87)",
                      }}
                    >
                      {option.shortLabel ?? option.label}
                    </Typography>
                  }
                  secondary={
                    option.sublabel ? (
                      <Typography
                        sx={{
                          fontSize: typography.Size.Text.xs.$value,
                          lineHeight: typography.Line_height.text.xs.$value,
                          fontWeight: typography.Font_weight.Regular.$value,
                          color: "rgba(0,0,0,0.60)",
                        }}
                      >
                        {option.sublabel}
                      </Typography>
                    ) : undefined
                  }
                />
              </ListItem>
              {index < additionalOptions.length - 1 && (
                <Divider sx={{ borderColor: "rgba(0,0,0,0.08)" }} />
              )}
            </React.Fragment>
          ))}
        </List>
      </Box>

      <Button
        data-tracker="809"
        fullWidth
        variant="contained"
        sx={{
          textTransform: "capitalize",
          boxShadow: "none !important",
          background: colors.Brand[600].$value,
          borderRadius: 2,
        }}
        onClick={handleApply}
      >
        Apply
      </Button>
    </Box>
  );

  return (
    <>
      <IconButton
        data-tracker="808"
        disabled={isMtd}
        sx={{
          borderRadius: spacing.Radius.full.$value,
          backgroundColor: isComparisonActive ? colors.Brand[50].$value : "transparent",
          "&:hover": {
            backgroundColor: isComparisonActive
              ? colors.Brand[100].$value
              : colors.Brand[50].$value,
          },
        }}
        color="secondary"
        size="large"
        onClick={handleIconClick}
      >
        <CompareIcon
          sx={{
            color: isMtd
              ? colors.GrayLightMode[300].$value
              : isComparisonActive
                ? colors.Brand[600].$value
                : colors.GrayLightMode[700].$value,
          }}
        />
      </IconButton>

      <Drawer
        anchor="bottom"
        open={open}
        onClose={toggleDrawer(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: spacing.Radius.xl.$value,
            borderTopRightRadius: spacing.Radius.xl.$value,
            p: spacing.Spacing.xl.$value + "!important",
          },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
}
